import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@pearsontechnology/aero-components';

import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import Modal, {
  ModalContent,
  ModalBodyText,
  ModalActions,
} from 'components/Modal';
import { featureKeys } from 'services/FeatureFlagService';
import WorkspaceService from 'services/WorkspaceService';
import messages from './messages';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';

const DuplicateWarningModal = ({
  onDismiss,
  showDuplicateModal,
  isDuplicateTargetLocationSame,
  handleDuplicateProcess,
  activityService,
  selectedActivityId,
  selectedWorkspace,
  getFeatureFlagValue = () => {},
}) => {
  const [activityInfo, setActivityInfo] = useState({});
  const isProjectDeepcopyFlagEnabled = getFeatureFlagValue(featureKeys.PROJECT_ACTIVITY_DUPLICATE_DEEPCOPY);
  const isWorkspaceDeepcopyFlagEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_ACTIVITY_DUPLICATE_DEEPCOPY);
  useEffect(() => {
    async function fetchActivityDetails() {
      const { config: { title }, plugin: { type } } = await activityService.getActivity(selectedActivityId);
      setActivityInfo({ title, type });
    }

    if (selectedActivityId) {
      fetchActivityDetails();
    }
  }, [selectedActivityId, activityService]);

  const getFormattedMessage = (msg) => {
    const msgConfig = {
      __html: intl.formatMessage(msg, { elementType: activityInfo.type }),
    };
    return (
      <>
        <span dangerouslySetInnerHTML={msgConfig}></span>
        <div>
          <em>{intl.formatMessage(messages.refreshNote)}</em>
        </div>
      </>
    );
  };

  const getModalContent = () => {
    //workspaceid in which user is duplicating course.
    const currentWorkspaceId = WorkspaceService.getSelectedWorkspace().id;
    //workspace in which course is getting duplicated.
    const userSelecetedWorkspace = selectedWorkspace?.id;
    let isShallowCopyMessage = false;
    const isSelectedWorkspaceSame = userSelecetedWorkspace === currentWorkspaceId;
    switch (true) {
      case isDuplicateTargetLocationSame:
        isShallowCopyMessage = true;
        break;
      case isProjectDeepcopyFlagEnabled && isWorkspaceDeepcopyFlagEnabled:
        isShallowCopyMessage = false;
        break;
      case isProjectDeepcopyFlagEnabled && isSelectedWorkspaceSame:
        isShallowCopyMessage = false;
        break;
      case isWorkspaceDeepcopyFlagEnabled && !isSelectedWorkspaceSame:
        isShallowCopyMessage = false;
        break;
      default:
        isShallowCopyMessage = true;
        break;
    }
    return isShallowCopyMessage ? getFormattedMessage(messages.shallowCopyWarningForDuplicate) : getFormattedMessage(messages.deepCopyWarningForDuplicate);
  };
  
  return (
    <>
      <Modal
        title={intl.formatMessage(messages.duplicateWarningTitle, { elementTitle: activityInfo.title, elementType: activityInfo.type })}
        icon='warningCircle'
        onDismiss={onDismiss}
        visible={showDuplicateModal}
      >
        <ModalContent>
          <ModalBodyText>
            {getModalContent()}
          </ModalBodyText>
        </ModalContent>
        <ModalActions>
          <Button grey onClick={onDismiss}>
            {intl.formatMessage(messages.cancel)}
          </Button>
          <Button
            primary
            onClick={handleDuplicateProcess}
          >
            {intl.formatMessage(messages.duplicate)}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

DuplicateWarningModal.propTypes = {
  onDismiss: PropTypes.func.isRequired,
  showDuplicateModal: PropTypes.bool,
  isDuplicateTargetLocationSame: PropTypes.bool,
  handleDuplicateProcess: PropTypes.func.isRequired,
  activityService: PropTypes.object,
  selectedActivityId: PropTypes.string,
};

const flagNames = [featureKeys.PROJECT_ACTIVITY_DUPLICATE_DEEPCOPY, featureKeys.WORKSPACE_ACTIVITY_DUPLICATE_DEEPCOPY];
export default enhanceWithFeatureFlagSubscription(DuplicateWarningModal, flagNames);
